import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, X, Bell, LogOut, ChevronDown, QrCode, Search, User as UserIcon, KeyRound, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '../utils/cn';
import { useAuth } from '../hooks/useAuth';
import { NAV_GROUPS, TOP_NAV_ITEMS, QR_NAV_ITEM, REPORTS_NAV_ITEM, hasNavAccess } from './navConfig';
import type { NavItem } from './navConfig';
import { dashboardApi, notificationsApi } from '../services/resources';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { PendingApprovalsModal } from '../components/PendingApprovalsModal';
import { getAppearance, syncAppearance } from '../utils/appearance';
import { logoUrl, useSite } from '../utils/site';
import { syncChartSettings } from '../features/charts/chartSettings';
import { Marquee } from '../public/components/Motion';
import '../public/motion.css';

const SIDEBAR_COLLAPSED_KEY = 'ilms.sidebarCollapsed';

// This browser's own choice, else the farm's default (Administration -> Appearance).
function readSidebarCollapsed(): boolean {
  try {
    const own = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (own !== null) return own === '1';
  } catch {
    // fall through to the default
  }
  return getAppearance().sidebar_default === 'hidden';
}

// `compact` = the collapsed icon-only rail: labels hidden (kept as hover
// titles), group headings swapped for a thin divider.
function SidebarContent({ onNavigate, compact = false }: { onNavigate?: () => void; compact?: boolean }) {
  const { roles, permissions } = useAuth();
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-2.5 rounded-md h-11 text-sm font-medium transition-colors [&_svg]:h-[18px] [&_svg]:w-[18px]',
      compact ? 'justify-center px-0' : 'px-3',
      isActive ? 'bg-brand-green-50 text-brand-green-800' : 'text-ink-700 hover:bg-surface-muted'
    );
  const label = (text: string) => (compact ? <span className="sr-only">{text}</span> : text);
  // Hides a nav item entirely rather than showing a page that will just
  // 403 after the click — a Role never granting it, or an Admin/Super
  // Admin's Page Access restriction (see navConfig.ts's NAV_ITEM_MODULES),
  // reads the same way here: simply not shown.
  const canAccessNavItem = (item: NavItem) => hasNavAccess(item.to, roles, permissions);

  return (
    <nav className={cn('flex flex-col gap-5 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0', compact ? 'px-2' : 'px-3')}>
      <div>
        {TOP_NAV_ITEMS.filter(canAccessNavItem).map((item) => (
          <NavLink key={item.to} to={item.to} end className={linkClass} onClick={onNavigate} title={compact ? item.label : undefined}>
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {label(item.label)}
          </NavLink>
        ))}
        {canAccessNavItem(REPORTS_NAV_ITEM) && (
          <NavLink to={REPORTS_NAV_ITEM.to} className={linkClass} onClick={onNavigate} title={compact ? REPORTS_NAV_ITEM.label : undefined}>
            <REPORTS_NAV_ITEM.icon className="h-[18px] w-[18px] shrink-0" />
            {label(REPORTS_NAV_ITEM.label)}
          </NavLink>
        )}
      </div>

      {canAccessNavItem(QR_NAV_ITEM) && (
        <NavLink
          to={QR_NAV_ITEM.to}
          onClick={onNavigate}
          title={compact ? 'Scan QR' : undefined}
          className="flex items-center justify-center gap-2 rounded-md bg-brand-gold-500 text-white h-11 font-semibold text-sm hover:bg-brand-gold-600 shrink-0"
        >
          <QrCode className="h-[18px] w-[18px]" /> {label('Scan QR')}
        </NavLink>
      )}

      {NAV_GROUPS.map((group) => {
        const visibleItems = group.items.filter(canAccessNavItem);
        if (visibleItems.length === 0) return null;
        return (
          <div key={group.label}>
            {compact ? (
              <div className="border-t border-ink-300/20 mx-2 mb-1.5" aria-hidden="true" />
            ) : (
              <p className="text-helper-text uppercase tracking-wide text-ink-400 px-3 mb-1.5">{group.label}</p>
            )}
            <div className="flex flex-col gap-0.5">
              {visibleItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass} onClick={onNavigate} title={compact ? item.label : undefined}>
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  {label(item.label)}
                </NavLink>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const { user, roles, can, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop only: the header's arrow button hides the sidebar completely so
  // pages get the full width, and shows it again - click only (on request),
  // no hover pop-out. Remembered per browser.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const site = useSite();
  const notices = site.app_notice_show ? site.app_notice_items : [];
  // Pick up an Admin's Appearance change (reloads once if it differs from this browser's copy).
  useEffect(() => { syncAppearance(); syncChartSettings(); }, []);
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, prev ? '0' : '1');
      } catch {
        // Storage blocked - the toggle still works for this session.
      }
      return !prev;
    });
  };
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Dismissed for the rest of this SPA session once closed/reviewed — this
  // component stays mounted across every page navigation after login, so a
  // plain useState (no persistence) already gives the "once per login"
  // behaviour ChangePasswordModal's own force-flag relies on too.
  const [pendingApprovalsDismissed, setPendingApprovalsDismissed] = useState(false);

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    retry: 0,
    staleTime: 60_000,
  });
  const unreadCount = unreadQuery.data ?? 0;

  const canApproveMilk = can('Milk', 'Approve');
  const kpisQuery = useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () => dashboardApi.kpis(),
    enabled: canApproveMilk,
    staleTime: 60_000,
  });
  // Sessions (milk coming in) + sales (milk going out) share one combined
  // count here - both sit behind the same Milk:Approve gate and the same
  // "nothing counts until approved" rule, so one popup covers both.
  const pendingMilkApprovals = (kpisQuery.data?.pending_milk_approvals ?? 0) + (kpisQuery.data?.pending_milk_sale_approvals ?? 0);
  const showPendingApprovalsModal = canApproveMilk && pendingMilkApprovals > 0 && !pendingApprovalsDismissed;

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="h-screen flex flex-col bg-surface-subtle overflow-hidden">
      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-ink-900/50" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-popover flex flex-col">
            <div className="h-16 flex items-center justify-between gap-2.5 px-4 border-b border-ink-300/15 shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <img src={logoUrl(site)} alt="Logo" className="h-11 w-11 object-contain shrink-0" />
                <p className="text-card-title truncate">{getAppearance().header_title}</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-surface-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Top bar — full width and pinned; collapsing the sidebar below never changes it */}
      <header className="h-16 shrink-0 border-b border-ink-300/15 bg-white flex items-center gap-3 px-3 sm:px-4">
        {/* Fixed-width brand block on desktop so the rest of the bar lines up with the expanded sidebar */}
        <div className="flex items-center gap-2 shrink-0 min-w-0 lg:w-60">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="lg:hidden h-11 w-11 flex items-center justify-center rounded-md hover:bg-surface-muted shrink-0"
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Show menu' : 'Hide menu'}
            title={sidebarCollapsed ? 'Show menu' : 'Hide menu'}
            className="hidden lg:flex h-10 w-10 items-center justify-center rounded-md text-ink-500 hover:bg-surface-muted hover:text-ink-900 shrink-0"
          >
            {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <img src={logoUrl(site)} alt="Logo" className="h-12 w-12 object-contain shrink-0" />
          <div className="hidden sm:block min-w-0">
            <p className="text-card-title truncate leading-tight">{getAppearance().header_title}</p>
            {getAppearance().header_subtitle && <p className="text-helper-text text-ink-400 truncate leading-tight">{getAppearance().header_subtitle}</p>}
          </div>
        </div>

        <div className="hidden md:flex items-center flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <button
              onClick={() => navigate('/qr-scan')}
              className="w-full h-10 rounded-md border border-ink-300/40 bg-surface-subtle pl-9 pr-3 text-left text-secondary text-ink-400 hover:border-brand-green-400"
            >
              Search by Tag / ID / QR…
            </button>
          </div>
        </div>

        <div className="flex-1 md:flex-none" />

        <NavLink
          to={QR_NAV_ITEM.to}
          className="hidden sm:inline-flex md:hidden items-center gap-1.5 rounded-md bg-brand-gold-500 text-white h-10 px-3 text-sm font-semibold hover:bg-brand-gold-600 shrink-0"
        >
          <QrCode className="h-4 w-4" /> Scan
        </NavLink>

        <button
          onClick={() => navigate('/notifications')}
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          className="relative h-11 w-11 flex items-center justify-center rounded-md hover:bg-surface-muted shrink-0"
        >
          <Bell className="h-5 w-5 text-ink-600" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-0.5 rounded-full bg-critical-600 text-white text-[10px] leading-4 text-center font-semibold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        <div className="relative shrink-0">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2 h-11 pl-1.5 pr-2 rounded-md hover:bg-surface-muted"
          >
            <span className="h-8 w-8 rounded-full bg-brand-green-100 text-brand-green-800 flex items-center justify-center font-semibold text-sm">
              {user?.username?.charAt(0)?.toUpperCase() ?? <UserIcon className="h-4 w-4" />}
            </span>
            <span className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-sm font-medium text-ink-900 max-w-[10rem] truncate">{user?.username ?? 'User'}</span>
              <span className="text-helper-text text-ink-400">{roles[0] ?? ''}</span>
            </span>
            <ChevronDown className="h-4 w-4 text-ink-400 hidden sm:block" />
          </button>
          {userMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-52 bg-white rounded-md border border-ink-300/20 shadow-popover z-20 py-1">
                <div className="px-3 py-2 border-b border-ink-300/10">
                  <p className="text-sm font-medium truncate">{user?.username}</p>
                  <p className="text-helper-text text-ink-400 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    setChangePasswordOpen(true);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted flex items-center gap-2"
                >
                  <KeyRound className="h-4 w-4" /> Change Password
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-surface-muted flex items-center gap-2 text-critical-600"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* A scrolling notice for staff, set on Administration -> Appearance -> Website & News */}
      {notices.length > 0 && (
        <div className="shrink-0 bg-brand-gold-50 border-b border-brand-gold-200 text-brand-gold-900" role="region" aria-label="Notice">
          <Marquee seconds={site.app_notice_seconds} className="py-1.5">
            {notices.map((text, i) => (
              <span key={i} className="flex items-center gap-3 px-6 text-secondary font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-gold-500" aria-hidden="true" />
                {text}
              </span>
            ))}
          </Marquee>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        {/* Desktop sidebar, below the header - slides fully away when hidden.
            Its nav list scrolls internally. */}
        <aside
          aria-hidden={sidebarCollapsed}
          className={cn(
            'hidden lg:block relative shrink-0 h-full overflow-hidden transition-[width] duration-200',
            sidebarCollapsed ? 'w-0' : 'w-64'
          )}
        >
          <div className="absolute inset-y-0 left-0 w-64 flex flex-col border-r border-ink-300/15 bg-white">
            <SidebarContent />
          </div>
        </aside>

        {/* Width cap lifts when the sidebar is collapsed, so the freed space is actually used */}
        <main
          className={cn(
            'flex-1 min-w-0 overflow-y-auto p-3 sm:p-5 lg:p-6 w-full mx-auto',
            !sidebarCollapsed && 'max-w-[1600px]'
          )}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile floating Scan QR action */}
      <NavLink
        to={QR_NAV_ITEM.to}
        className="sm:hidden fixed bottom-4 right-4 z-30 h-14 w-14 rounded-full bg-brand-gold-500 text-white shadow-popover flex items-center justify-center"
        aria-label="Scan QR"
      >
        <QrCode className="h-6 w-6" />
      </NavLink>

      <ChangePasswordModal
        open={changePasswordOpen || !!user?.must_change_password}
        force={!!user?.must_change_password}
        onClose={() => setChangePasswordOpen(false)}
        onChanged={() => {
          updateUser({ must_change_password: false });
          setChangePasswordOpen(false);
        }}
      />

      <PendingApprovalsModal
        open={showPendingApprovalsModal}
        count={pendingMilkApprovals}
        onClose={() => setPendingApprovalsDismissed(true)}
        onReview={() => {
          setPendingApprovalsDismissed(true);
          navigate('/dairy');
        }}
      />
    </div>
  );
}
